<template>
    <div
        class="fixed w-screen h-screen top-0 left-0 bg-black/60 z-20 flex items-center justify-center cursor-pointer"
        @keydown.esc="hide"
        @click.self.exact="hide"
    >
        <div v-if="loading" class="flex items-center justify-center">
            <i class="loading loading-spinner loading-md"/>
        </div>
        <div v-if="!loading" class="rounded bg-base-100 shadow-lg cursor-auto flex flex-col w-[50em] p-3 z-40">
            <div>
                <div class="flex justify-between">
                    <div class="flex text-2xl items-center gap-2">
                        <i class="text-base-300 flex i-mdi-format-list-group"/>
                        Faktura {{ localInvoiceBalance.invoiceNo }} | {{ findSubAccountByUuid(localInvoiceBalance.subAccountUuid).name }}
                    </div>
                    <button class="i-ph-x btn" @click="hide" />
                </div>
                <div v-if="debtCollectionEnabled && isRegisteredInDebtCollector" class="py-3">
                    Fakturaen blir sendt til Kravia for inkasso. Dette kan ta noen øyeblikk.
                    <a
                        class="text-primary cursor-pointer"
                        target="_blank"
                        :href="resolveInvoiceUrl(localInvoiceBalance.debtCollectionId)"
                    > Følg opp i Kravia sin kundeportal
                    </a>
                </div>
                <div class="flex flex-col gap-3 pt-3">
                    <div class="inline-flex gap-2">
                        <general-input
                            v-model="formattedDueDate"
                            class="w-1/2"
                            label="Forfallsdato"
                            :disabled="true"
                        />
                        <general-input
                            v-model="formattedAmount"
                            class="w-1/2"
                            label="Fakturabeløp"
                            :disabled="true"
                        />
                    </div>
                    <div class="inline-flex gap-2">
                        <general-input
                            v-model="formattedInvoiceDate"
                            class="w-1/2"
                            label="Fakturadato"
                            :disabled="true"
                        />
                        <general-input
                            v-model="formattedBalance"
                            class="w-1/2"
                            label="Balanse"
                            :disabled="true"
                        />
                    </div>
                    <general-input
                        v-model="localInvoiceBalance.debtCollectionStatus"
                        label="Betalingsoppfølging"
                        :disabled="true"
                    />
                    <div v-if="debtCollectionEnabled" class="flex gap-2 w-full pt-2">
                        <div class="inline-flex min-w-64 items-center">
                            <label>Overstyr inkassoinnstillinger</label>
                            <input
                                id="'toggle-kravia-settings"
                                v-model="invoiceSpecificDebtCollectionSettings"
                                :disabled="isRegisteredInDebtCollector"
                                type="checkbox"
                                class="toggle mx-2 toggle-primary"
                            >
                        </div>
                        <SimpleSelect
                            v-model="selectedFirstReminder"
                            :options="reminderOptions"
                            :disabled="!invoiceSpecificDebtCollectionSettings || isRegisteredInDebtCollector"
                            placeholder="Velg første påminnelse"
                            label="name"
                            track-by="value"
                        />
                        <general-input
                            v-model="intervalDays"
                            class="w-1/2"
                            :disabled="!invoiceSpecificDebtCollectionSettings"
                            label="Dager før påminnelse"
                            data-cy="reminder-days-input"
                        />
                    </div>
                </div>
            </div>
            <div v-if="debtCollectionEnabled" class="inline-flex justify-end">
                <button class="btn btn-sm btn-primary mt-2" :disabled="isRegisteredInDebtCollector" @click="updateInvoiceSettings">Oppdater</button>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">

import {InvoiceBalance} from '@/invoice/InvoiceBalance'
import GeneralInput from '@/components/GeneralInput.vue'
import {findSubAccountByUuid} from '@/subaccount/subAccount.service'
import {formatCurrency, formattedDate} from '@/services/formatter.service'
import {getDebtCollectionSettings, setDebtCollectionSettings} from '@/invoice/invoice.service'
import {computed, onMounted, reactive, ref} from 'vue'
import {KraviaReminder, reminderOptions} from '@/debt-collection/KraviaReminder'
import SimpleSelect from '@/components/SimpleSelect.vue'
import {useSetting} from '@/setting/setting.service'
import {isLocal, isStaging} from '@/services/infrastructure/envhelper.service'
import {notifySuccess} from '@/services/infrastructure/notification.service'

const props = defineProps({
    invoiceBalance: {
        type: Object as () => InvoiceBalance,
        required: true
    }
})

const isRegisteredInDebtCollector = computed(() => {
    return !!localInvoiceBalance.debtCollectionId
})

const loading = ref(true)
const invoiceSpecificDebtCollectionSettings = ref(false)
const reminderType = ref('')
const intervalDays = ref(14)
const localInvoiceBalance = reactive({...props.invoiceBalance})
const formattedInvoiceDate = computed(() => formattedDate(localInvoiceBalance.date))
const formattedDueDate = computed(() => formattedDate(localInvoiceBalance.dueDate))
const formattedBalance = computed(() => formatCurrency(localInvoiceBalance.balance, 2))
const formattedAmount = computed(() => formatCurrency(localInvoiceBalance.amount, 2))

const selectedFirstReminder = computed({
    get: () => reminderOptions.find(option => option.value === reminderType.value),
    set: (value: { name: string, value: KraviaReminder }) => {
        reminderType.value = value.value
    }
})

const debtCollectionEnabled = computed(() => {
    if (!kraviaCreditorIdSetting.value) {
        return false
    }
    return kraviaCreditorIdSetting.value.value !== null
})

const fetchInvoiceSettings = async () => {
    const settings = await getDebtCollectionSettings(localInvoiceBalance.uuid)
    if (settings) {
        invoiceSpecificDebtCollectionSettings.value = settings.invoiceSpecificSettings
        reminderType.value = settings.reminderType
        intervalDays.value = settings.intervalDays
    }
    loading.value = false
}

const updateInvoiceSettings = async () => {
    const updatedSettings = {
        invoiceUuid: localInvoiceBalance.uuid,
        invoiceSpecificSettings: invoiceSpecificDebtCollectionSettings.value,
        reminderType: reminderType.value,
        intervalDays: intervalDays.value
    }
    await setDebtCollectionSettings(updatedSettings)
    notifySuccess({ title: 'Oppdatert', description: 'Innstillinger ble oppdatert.'})
    hide()
}

const {setting: kraviaCreditorIdSetting} = useSetting<string>('KRAVIA_CREDITOR_ID')

const emit = defineEmits(['close'])
const hide = () => {
    emit('close')
}

const resolveInvoiceUrl = (debtCollectionId: string) => {
    if (isStaging() || isLocal()) {
        return `https://app-qa.kravia.ai/invoices/${debtCollectionId}`
    }
    return `https://app.kravia.ai/invoices/${debtCollectionId}`
}

onMounted(async () => {
    await fetchInvoiceSettings()
})
</script>


<style scoped>
@reference "@/index.css";
@reference "tailwindcss";

.multiselect {
    align-self: center;
}
.multiselect--disabled {
    background: transparent;
}
</style>